import moment from "moment";

import { createContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { gettingTags } from "utils/Filter";
import { gettingSchedule } from "utils/Schedule";
import { getMonth } from "utils/Times";
import {
	buildTimekeeperSchedulePath,
	buildTimekeeperScheduleSearch,
	parseTimekeeperScheduleRoute,
} from "utils/timekeeperScheduleRouting";

const Context = createContext({});

const ContextProvider = ({ children, value }) => {
	const location = useLocation();
	const navigate = useNavigate();

	const [tableDateRange, setTableDateRange] = useState([
		moment(getMonth().start_date),
		moment(getMonth().end_date),
	]);

	// Filter
	const [tags, setTags] = useState([]);
	const [tag, setTag] = useState([]);
	const [filterTags, setFilterTags] = useState("");
	const [loadingGettingTags, setLoadingGettingTags] = useState(false);

	const [employees, setEmployees] = useState([]);
	const [employee, setEmployee] = useState([]);
	const [pendingEmployeeIds, setPendingEmployeeIds] = useState(null);
	const [pendingTagIds, setPendingTagIds] = useState(null);
	const [urlHydrated, setUrlHydrated] = useState(false);
	const [filterEmployees, setFilterEmployees] = useState("");
	const [loadingGettingEmployees, setLoadingGettingEmployees] =
		useState(false);

	// table
	const [rows, setRows] = useState([]);
	const [checked, setChecked] = useState([]);
	const [loadingGettingSchedule, setLoadingGettingSchedule] = useState(false);

	// Actions
	const [shiftId, setShiftId] = useState(null);
	const [openAddShift, setOpenAddShift] = useState(false);
	const [openEditShift, setOpenEditShift] = useState(false);
	const [openDeleteShift, setOpenDeleteShift] = useState(false);
	const [routeAddShiftDay, setRouteAddShiftDay] = useState(null);

	const reloading = async () => {
		console.log("employees", employees);

		await gettingSchedule(
			setLoadingGettingSchedule,
			setRows,
			employee,
			tableDateRange
		);
	};

	useEffect(() => {
		gettingTags(
			setLoadingGettingTags,
			setTags,
			setTag,
			setEmployees,
			setEmployee,
			() => {},
			"schedule",
			{
				start_date: tableDateRange[0].format("YYYY-MM-DD"),
				end_date: tableDateRange[1].format("YYYY-MM-DD"),
				without_hiring: false,
			}
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const parsed = parseTimekeeperScheduleRoute(
			location.pathname,
			location.search
		);

		setPendingEmployeeIds(parsed.employeeIds);
		setPendingTagIds(parsed.tagIds);

		if (parsed.from && parsed.to) {
			setTableDateRange([
				moment(parsed.from, "YYYY-MM-DD"),
				moment(parsed.to, "YYYY-MM-DD"),
			]);
		}

		setRouteAddShiftDay(parsed.day);

		if (parsed.action === "add-shift") {
			setOpenAddShift(true);
			setOpenEditShift(false);
			setOpenDeleteShift(false);
			setShiftId(null);
		} else if (parsed.action === "edit-shift" && parsed.shiftId !== null) {
			setShiftId(parsed.shiftId);
			setOpenEditShift(true);
			setOpenAddShift(false);
			setOpenDeleteShift(false);
		} else if (
			parsed.action === "delete-shift" &&
			parsed.shiftId !== null
		) {
			setShiftId(parsed.shiftId);
			setOpenDeleteShift(true);
			setOpenAddShift(false);
			setOpenEditShift(false);
		} else {
			setOpenAddShift(false);
			setOpenEditShift(false);
			setOpenDeleteShift(false);
			setShiftId(null);
		}

		setUrlHydrated(true);
	}, [location.pathname, location.search]);

	useEffect(() => {
		if (pendingTagIds === null || tags.length === 0) {
			return;
		}

		if (pendingTagIds.length === 0) {
			setPendingTagIds(null);
			return;
		}

		const matchedTags = tags.filter((tg) =>
			pendingTagIds.includes(Number(tg?.id))
		);

		if (matchedTags.length > 0) {
			setTag(matchedTags);
		}

		setPendingTagIds(null);
	}, [pendingTagIds, tags]);

	useEffect(() => {
		if (pendingEmployeeIds === null || employees.length === 0) {
			return;
		}

		if (pendingEmployeeIds.length === 0) {
			setPendingEmployeeIds(null);
			return;
		}

		const uniqueById = [];
		const seen = new Set();

		for (const emp of employees) {
			if (seen.has(emp?.id)) {
				continue;
			}

			seen.add(emp?.id);
			uniqueById.push(emp);
		}

		const matched = uniqueById.filter((emp) =>
			pendingEmployeeIds.includes(Number(emp?.id))
		);

		if (matched.length > 0) {
			setEmployee(matched);
			setChecked(matched.map((emp) => emp.id));
		}

		setPendingEmployeeIds(null);
	}, [employees, pendingEmployeeIds]);

	useEffect(() => {
		if (!urlHydrated) {
			return;
		}

		let action = null;
		if (openEditShift && shiftId) {
			action = "edit-shift";
		} else if (openDeleteShift && shiftId) {
			action = "delete-shift";
		} else if (openAddShift) {
			action = "add-shift";
		}

		const from =
			tableDateRange[0] && tableDateRange[0].isValid()
				? tableDateRange[0].format("YYYY-MM-DD")
				: null;
		const to =
			tableDateRange[1] && tableDateRange[1].isValid()
				? tableDateRange[1].format("YYYY-MM-DD")
				: null;

		const day =
			openAddShift && routeAddShiftDay
				? routeAddShiftDay
				: openAddShift
				  ? from
				  : null;

		const selectedEmployeeIds = [
			...new Set(employee.map((emp) => Number(emp?.id))),
		]
			.filter((id) => Number.isInteger(id))
			.sort((a, b) => a - b);
		const allEmployeeIds = [
			...new Set(employees.map((emp) => Number(emp?.id))),
		]
			.filter((id) => Number.isInteger(id))
			.sort((a, b) => a - b);

		const selectedTagIds = [...new Set(tag.map((tg) => Number(tg?.id)))]
			.filter((id) => Number.isInteger(id))
			.sort((a, b) => a - b);
		const allTagIds = [...new Set(tags.map((tg) => Number(tg?.id)))]
			.filter((id) => Number.isInteger(id))
			.sort((a, b) => a - b);

		const employeeIds =
			allEmployeeIds.length > 0 &&
			selectedEmployeeIds.length === allEmployeeIds.length
				? []
				: selectedEmployeeIds;
		const tagIds =
			allTagIds.length > 0 && selectedTagIds.length === allTagIds.length
				? []
				: selectedTagIds;

		const targetPath = buildTimekeeperSchedulePath({
			action,
			shiftId,
		});
		const targetSearch = buildTimekeeperScheduleSearch({
			from,
			to,
			day,
			employeeIds,
			tagIds,
		});

		if (
			location.pathname !== targetPath ||
			location.search !== targetSearch
		) {
			navigate(`${targetPath}${targetSearch}`, { replace: true });
		}
	}, [
		employee,
		location.pathname,
		location.search,
		navigate,
		employees,
		openAddShift,
		openDeleteShift,
		openEditShift,
		routeAddShiftDay,
		shiftId,
		tags,
		tag,
		tableDateRange,
		urlHydrated,
	]);

	return (
		<Context.Provider
			value={{
				...value,
				tableDateRange,
				setTableDateRange,
				tags,
				setTags,
				tag,
				setTag,
				filterTags,
				setFilterTags,
				loadingGettingTags,
				setLoadingGettingTags,
				employees,
				setEmployees,
				employee,
				setEmployee,
				filterEmployees,
				setFilterEmployees,
				loadingGettingEmployees,
				setLoadingGettingEmployees,
				loadingGettingSchedule,
				setLoadingGettingSchedule,
				rows,
				setRows,
				openAddShift,
				setOpenAddShift,
				openEditShift,
				setOpenEditShift,
				openDeleteShift,
				setOpenDeleteShift,
				routeAddShiftDay,
				setRouteAddShiftDay,
				checked,
				setChecked,
				shiftId,
				setShiftId,
				reloading,
			}}
		>
			{children}
		</Context.Provider>
	);
};

export { Context, ContextProvider };
